import { motion, useInView, useScroll, useTransform } from 'framer-motion'
import { Building2, Zap, Truck, Leaf, Fuel, Recycle, TrendingUp, ArrowRight, ArrowLeft, Play } from 'lucide-react'
import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import servicesImage from '../assets/Services/services1.avif'
import '../styles/glassmorphism.css'

const Services = () => {
  const { t, i18n } = useTranslation()
  const [activeStation, setActiveStation] = useState(3)
  const [isAnimating, setIsAnimating] = useState(false)
  const containerRef = useRef(null)
  const isInView = useInView(containerRef, { once: true, margin: "-100px" })
  const navigate = useNavigate()
  const isRTL = i18n.language === 'ar' || i18n.language === 'ku'

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  })

  const stations = [
    {
      id: 1,
      number: '01',
      icon: Building2,
      title: t('header.servicesList.construction'),
      subtitle: 'Building Excellence',
      description: t('services.serviceDescriptions.construction'),
      metric: '150+',
      label: t('services.ecosystem.stats.projectsCompleted'),
      x: 5,
      y: 35,
      route: '/services/construction',
    },
    {
      id: 2,
      number: '02',
      icon: Zap,
      title: t('header.servicesList.powerGeneration'),
      subtitle: 'Energy Solutions',
      description: t('services.serviceDescriptions.powerGeneration'),
      metric: '50MW',
      label: t('services.ecosystem.cardLabels.totalCapacity'),
      x: 18,
      y: 60,
      route: '/services/power-generation',
    },
    {
      id: 3,
      number: '03',
      icon: Leaf,
      title: t('header.servicesList.environmental'),
      subtitle: 'Sustainability',
      description: t('services.serviceDescriptions.environmental'),
      metric: '100+',
      label: t('services.ecosystem.cardLabels.environmentalProjects'),
      x: 35,
      y: 75,
      route: '/services/environmental',
      isCenter: true
    },
    {
      id: 4,
      number: '04',
      icon: Fuel,
      title: t('header.servicesList.oilFuelSupply'),
      subtitle: 'Energy Distribution',
      description: t('services.serviceDescriptions.oilFuelSupply'),
      metric: '5M+',
      label: t('services.ecosystem.cardLabels.litersSupplied'),
      x: 52,
      y: 45,
      route: '/services/oil-fuel-supply',
    },
    {
      id: 5,
      number: '05',
      icon: Truck,
      title: t('header.servicesList.logistics'),
      subtitle: 'Supply Chain',
      description: t('services.serviceDescriptions.logistics'),
      metric: '500K+',
      label: t('services.ecosystem.cardLabels.tonsTransported'),
      x: 68,
      y: 30,
      route: '/services/logistics',
    },
    {
      id: 6,
      number: '06',
      icon: TrendingUp,
      title: t('header.servicesList.generalTrading'),
      subtitle: 'Market Solutions',
      description: t('services.serviceDescriptions.generalTrading'),
      metric: '$50M+',
      label: t('services.ecosystem.cardLabels.annualVolume'),
      x: 82,
      y: 55,
      route: '/services/general-trading',
    },
    {
      id: 7,
      number: '07',
      icon: Recycle,
      title: t('header.servicesList.recycling'),
      subtitle: 'Circular Economy',
      description: t('services.serviceDescriptions.recycling'),
      metric: '10K+',
      label: t('services.ecosystem.cardLabels.tonsRecycled'),
      x: 90,
      y: 80,
      route: '/services/recycling',
    }
  ]

  // Auto-cycle through stations
  useEffect(() => {
    if (!isInView) return

    const interval = setInterval(() => {
      setIsAnimating(true)
      setTimeout(() => {
        setActiveStation((prev) => (prev % stations.length) + 1)
        setIsAnimating(false)
      }, 400)
    }, 5000)

    return () => clearInterval(interval)
  }, [isInView, stations.length])

  // Create smooth curved path
  const createPath = () => {
    let path = `M ${stations[0].x} ${stations[0].y}`

    for (let i = 0; i < stations.length - 1; i++) {
      const current = stations[i]
      const next = stations[i + 1]

      const cp1x = current.x + (next.x - current.x) * 0.35
      const cp1y = current.y + 12
      const cp2x = current.x + (next.x - current.x) * 0.65
      const cp2y = next.y + 12

      path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${next.x} ${next.y}`
    }

    return path
  }

  const currentStation = stations.find(s => s.id === activeStation)

  return (
    <section
      ref={containerRef}
      id="services"
      className="relative min-h-screen py-16 sm:py-20 lg:py-32 overflow-hidden"
    >
      {/* Hero-like Background */}
      <div className="absolute inset-0">
        <img
          src={servicesImage}
          alt="Services"
          className="w-full h-full object-cover"
        />

        {/* Image overlays matching hero */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
      </div>



      <div className="relative z-20 max-w-[1600px] mx-auto px-6 sm:px-8 lg:px-12">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mb-8 sm:mb-12 lg:mb-24"
        >
          <div className="space-y-4 sm:space-y-6">
            <div className="text-white/80 text-sm min-[390px]:text-base min-[430px]:text-lg sm:text-base font-semibold tracking-[0.15em] uppercase">
              {t('services.ecosystem.capabilities')}
            </div>
            <h2 className="text-3xl min-[390px]:text-4xl min-[430px]:text-5xl sm:text-5xl md:text-6xl lg:text-6xl xl:text-7xl font-medium text-white leading-[0.95] tracking-tight">
              {t('services.ecosystem.title')}{' '}
              <span className="block mt-1 sm:mt-2">{t('services.ecosystem.titleHighlight')}</span>
            </h2>
            <p className="text-base min-[390px]:text-lg min-[430px]:text-xl sm:text-xl md:text-xl lg:text-xl text-white/90 font-normal leading-relaxed max-w-2xl">
              {t('services.ecosystem.description')}
            </p>
          </div>
        </motion.div>

        {/* Journey Visualization */}
        <div className="relative h-[240px] min-[375px]:h-[280px] min-[390px]:h-[300px] min-[430px]:h-[340px] sm:h-[400px] md:h-[480px] lg:h-[650px] mb-8 sm:mb-12 lg:mb-16">

          {/* SVG Path System */}
          <svg
            className="absolute inset-0 w-full h-full"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            style={{ overflow: 'visible' }}
          >
            <defs>
              <linearGradient id="pathGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="rgba(255,255,255,0.2)" />
                <stop offset="50%" stopColor="rgba(255,255,255,0.4)" />
                <stop offset="100%" stopColor="rgba(255,255,255,0.2)" />
              </linearGradient>

              <filter id="glow">
                <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                <feMerge>
                  <feMergeNode in="coloredBlur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            {/* Base path */}
            <motion.path
              d={createPath()}
              fill="none"
              stroke="rgba(255,255,255,0.1)"
              strokeWidth="0.3"
              strokeLinecap="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: isInView ? 1 : 0 }}
              transition={{ duration: 2.5, ease: [0.43, 0.13, 0.23, 0.96] }}
            />

            {/* Animated glowing path */}
            <motion.path
              d={createPath()}
              fill="none"
              stroke="url(#pathGradient)"
              strokeWidth="0.4"
              strokeLinecap="round"
              filter="url(#glow)"
              strokeDasharray="100"
              initial={{ strokeDashoffset: 100 }}
              animate={{ strokeDashoffset: isInView ? 0 : 100 }}
              transition={{ duration: 3, ease: [0.43, 0.13, 0.23, 0.96], delay: 0.5 }}
            />
          </svg>

          {/* Station Points */}
          {stations.map((station, index) => (
            <motion.div
              key={station.id}
              initial={{ scale: 0, opacity: 0 }}
              animate={isInView ? { scale: 1, opacity: 1 } : { scale: 0, opacity: 0 }}
              transition={{
                duration: 0.6,
                delay: 0.8 + index * 0.12,
                type: "spring",
                stiffness: 200
              }}
              style={{
                position: 'absolute',
                left: `${station.x}%`,
                top: `${station.y}%`,
                transform: 'translate(-50%, -50%)'
              }}
              className="cursor-pointer relative touch-manipulation"
              onClick={() => {
                navigate(station.route)
              }}
              onMouseEnter={() => setActiveStation(station.id)}
              onTouchStart={() => setActiveStation(station.id)}
              onMouseLeave={() => { }}
            >
              {/* Pulse effect */}
              {activeStation === station.id && (
                <motion.div
                  className="absolute inset-0 rounded-full bg-white z-0"
                  initial={{ scale: 1, opacity: 0.3 }}
                  animate={{
                    scale: [1, 2.5, 3],
                    opacity: [0.3, 0.15, 0]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeOut"
                  }}
                />
              )}

              {/* Main station node */}
              <motion.div
                className={`relative rounded-full flex items-center justify-center backdrop-blur-xl border-2 shadow-2xl ${station.isCenter
                    ? 'w-10 h-10 min-[375px]:w-12 min-[375px]:h-12 min-[390px]:w-14 min-[390px]:h-14 min-[430px]:w-16 min-[430px]:h-16 sm:w-18 sm:h-18 md:w-20 md:h-20 lg:w-22 lg:h-22'
                    : 'w-8 h-8 min-[375px]:w-9 min-[375px]:h-9 min-[390px]:w-10 min-[390px]:h-10 min-[430px]:w-12 min-[430px]:h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 lg:w-18 lg:h-18'
                  }`}
                style={{
                  backgroundColor: activeStation === station.id ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.1)',
                  borderColor: activeStation === station.id ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.2)',
                  zIndex: activeStation === station.id ? 10 : 20
                }}
                animate={{
                  scale: activeStation === station.id ? 1.15 : 1,
                  boxShadow: activeStation === station.id
                    ? '0 20px 60px rgba(255,255,255,0.3)'
                    : '0 10px 30px rgba(0,0,0,0.3)'
                }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              >
                <motion.div
                  animate={{
                    color: activeStation === station.id ? '#000000' : '#ffffff'
                  }}
                  transition={{ duration: 0.3 }}
                  className="flex items-center justify-center"
                >
                  <station.icon className={station.isCenter ? "w-3 h-3 min-[375px]:w-4 min-[375px]:h-4 min-[390px]:w-5 min-[390px]:h-5 min-[430px]:w-6 min-[430px]:h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 lg:w-9 lg:h-9" : "w-2.5 h-2.5 min-[375px]:w-3 min-[375px]:h-3 min-[390px]:w-3.5 min-[390px]:h-3.5 min-[430px]:w-4 min-[430px]:h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 lg:w-7 lg:h-7"} />
                </motion.div>
              </motion.div>

              {/* Info card */}
              <motion.div
                initial={{ opacity: 0, y: -15, scale: 0.9 }}
                animate={activeStation === station.id && !isAnimating ? {
                  opacity: 1,
                  y: 0,
                  scale: 1
                } : {
                  opacity: 0,
                  y: -15,
                  scale: 0.9
                }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className={`absolute bg-white/5 backdrop-blur-2xl border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.2)] rounded-lg sm:rounded-xl lg:rounded-2xl p-2.5 min-[375px]:p-3 min-[390px]:p-3.5 min-[430px]:p-4 sm:p-4 lg:p-6 min-w-[140px] min-[375px]:min-w-[160px] min-[390px]:min-w-[180px] min-[430px]:min-w-[200px] sm:min-w-[240px] lg:min-w-[280px] max-w-[65vw] min-[375px]:max-w-[70vw] min-[390px]:max-w-[75vw] min-[430px]:max-w-[78vw] sm:max-w-[85vw] z-[100] pointer-events-none ${station.id === 5
                    ? 'bottom-full mb-4 min-[375px]:mb-5 min-[390px]:mb-6 min-[430px]:mb-8 sm:mb-10 lg:mb-12 right-full mr-1.5 min-[375px]:mr-2 min-[390px]:mr-2.5 min-[430px]:mr-3 sm:mr-4'
                    : station.y < 40
                      ? `top-full mt-2.5 min-[375px]:mt-3 min-[390px]:mt-3.5 min-[430px]:mt-4 sm:mt-6 lg:mt-8 ${station.x < 20 ? 'left-0' : station.x > 80 ? 'right-0' : 'left-1/2 -translate-x-1/2'
                      }`
                      : `bottom-full mb-2.5 min-[375px]:mb-3 min-[390px]:mb-3.5 min-[430px]:mb-4 sm:mb-6 lg:mb-8 ${station.x < 20 ? 'left-0' : station.x > 80 ? 'right-0' : 'left-1/2 -translate-x-1/2'
                      }`
                  }`}
              >
                <div className="flex items-center gap-2 mb-3 sm:mb-4">
                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-white" />
                  <div className="text-[10px] min-[390px]:text-xs min-[430px]:text-xs sm:text-xs text-white/60 uppercase tracking-[0.15em] font-medium">
                    {t('services.ecosystem.service')} {station.number}
                  </div>
                </div>

                <div className="mb-3 sm:mb-4">
                  <div className="text-2xl min-[390px]:text-3xl min-[430px]:text-4xl sm:text-3xl md:text-4xl lg:text-4xl font-light text-white mb-1">
                    {station.metric}
                  </div>
                  <div className="text-sm min-[390px]:text-base min-[430px]:text-lg sm:text-base text-white/70 font-medium tracking-wide">
                    {station.label}
                  </div>
                </div>

                <h3 className="text-base min-[390px]:text-lg min-[430px]:text-xl sm:text-xl font-medium text-white mb-2">
                  {station.title}
                </h3>
                <p className="text-sm min-[390px]:text-base min-[430px]:text-lg sm:text-base text-white/90 font-normal leading-relaxed mb-3 sm:mb-4">
                  {station.description}
                </p>

                {station.isCenter && (
                  <div className="flex items-center gap-2 pt-3 sm:pt-4 border-t border-white/10">
                    <div className="w-6 h-6 min-[390px]:w-7 min-[390px]:h-7 sm:w-8 sm:h-8 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center">
                      <Play className="w-3 h-3 min-[390px]:w-3.5 min-[390px]:h-3.5 sm:w-4 sm:h-4 text-white" />
                    </div>
                    <span className="text-sm min-[390px]:text-base min-[430px]:text-lg sm:text-base text-white/70 font-medium">{t('services.ecosystem.featuredService')}</span>
                  </div>
                )}

                {/* Card pointer */}
                <div className={`absolute w-2 h-2 min-[375px]:w-2.5 min-[375px]:h-2.5 min-[390px]:w-3 min-[390px]:h-3 min-[430px]:w-3.5 min-[430px]:h-3.5 sm:w-4 sm:h-4 bg-white/5 backdrop-blur-2xl border border-white/10 rotate-45 ${station.id === 5
                    ? '-bottom-1 min-[375px]:-bottom-1.5 min-[390px]:-bottom-1.5 min-[430px]:-bottom-2 sm:-bottom-2 -right-0.5 min-[375px]:-right-1 min-[390px]:-right-1.5 min-[430px]:-right-1.5 sm:-right-2'
                    : station.y < 40
                      ? '-top-1 min-[375px]:-top-1.5 min-[390px]:-top-1.5 min-[430px]:-top-2 sm:-top-2 left-1/2 -translate-x-1/2'
                      : station.x > 50
                        ? '-bottom-1 min-[375px]:-bottom-1.5 min-[390px]:-bottom-1.5 min-[430px]:-bottom-2 sm:-bottom-2 right-3 min-[375px]:right-4 min-[430px]:right-5 sm:right-8 lg:right-8'
                        : '-bottom-1 min-[375px]:-bottom-1.5 min-[390px]:-bottom-1.5 min-[430px]:-bottom-2 sm:-bottom-2 left-1/2 -translate-x-1/2'
                  }`} />
              </motion.div>

              {/* Label */}
              <motion.div
                animate={{ opacity: activeStation === station.id ? 0 : 1 }}
                transition={{ duration: 0.2 }}
                className="absolute -bottom-8 min-[390px]:-bottom-10 left-1/2 -translate-x-1/2 text-center whitespace-nowrap z-10"
              >
                <div className="text-sm min-[390px]:text-base min-[430px]:text-lg sm:text-base font-medium text-white/70 tracking-wide hidden sm:block">
                  {station.title}
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>

        {/* Bottom Section */}
        <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-end">
          {/* Left - Stats */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8, delay: 1.5 }}
            className="grid grid-cols-3 gap-3 min-[390px]:gap-4 sm:gap-6 lg:gap-8 relative z-20"
          >
            {[
              { value: '230+', label: 'Projects Completed' },
              { value: '14+', label: 'Years Experience' },
              { value: '98%', label: 'Client Satisfaction' }
            ].map((stat, idx) => {
              const labels = [t('services.ecosystem.stats.projectsCompleted'), t('services.ecosystem.stats.yearsExperience'), t('services.ecosystem.stats.clientSatisfaction')]
              return (
                <div key={idx} className="text-center lg:text-left">
                  <div className="text-2xl min-[390px]:text-3xl min-[430px]:text-4xl sm:text-3xl md:text-4xl lg:text-4xl font-light text-white mb-1 sm:mb-2">{stat.value}</div>
                  <div className="text-sm min-[390px]:text-base min-[430px]:text-lg sm:text-base text-white/70 font-medium tracking-wide leading-tight">{labels[idx]}</div>
                </div>
              )
            })}
          </motion.div>

          {/* Right - CTA */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8, delay: 1.7 }}
            className="flex justify-center lg:justify-end mt-4 sm:mt-6 lg:mt-0"
          >
            <button 
              onClick={() => navigate('/services')}
              className="group bg-white/5 backdrop-blur-2xl border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.2)] text-white px-8 py-4 sm:px-8 sm:py-4 rounded-full font-medium hover:bg-white/10 transition-all duration-300 flex items-center gap-3 sm:gap-3 justify-center text-base min-[390px]:text-lg min-[430px]:text-xl sm:text-base min-h-[56px] sm:min-h-[56px] touch-manipulation">
              {isRTL && <ArrowLeft className="w-5 h-5 min-[390px]:w-6 min-[390px]:h-6 min-[430px]:w-7 min-[430px]:h-7 sm:w-5 sm:h-5 group-hover:-translate-x-1 transition-transform duration-300" />}
              {t('services.ecosystem.exploreAll')}
              {!isRTL && <ArrowRight className="w-5 h-5 min-[390px]:w-6 min-[390px]:h-6 min-[430px]:w-7 min-[430px]:h-7 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform duration-300" />}
            </button>
          </motion.div>
        </div>

        {/* Progress Indicators */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.6, delay: 1.9 }}
          className="flex items-center justify-center gap-1 min-[390px]:gap-1.5 sm:gap-2 mt-6 sm:mt-8 lg:mt-16"
        >
          {stations.map((station) => (
            <button
              key={station.id}
              onClick={() => {
                navigate(station.route)
              }}
              className="relative group touch-manipulation"
            >
              <motion.div
                className="h-0.5 min-[390px]:h-1 sm:h-1 rounded-full backdrop-blur-2xl border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.2)]"
                style={{
                  width: station.isCenter ? '24px' : '18px',
                }}
                animate={{
                  backgroundColor: activeStation === station.id ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.2)',
                  width: activeStation === station.id
                    ? (station.isCenter ? '28px' : '22px')
                    : (station.isCenter ? '24px' : '18px')
                }}
                transition={{ duration: 0.3 }}
              />

              {/* Tooltip */}
              <div className="absolute -top-8 min-[390px]:-top-10 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
                <div className="bg-white text-black text-sm min-[390px]:text-base min-[430px]:text-lg sm:text-base px-3 py-1.5 rounded-lg font-medium">
                  {station.title}
                </div>
              </div>
            </button>
          ))}
        </motion.div>
      </div>
    </section>
  )
}

export default Services
